<template>
  <Dialog :open="props.open" @update:open="onClose">
    <DialogContent class="max-w-[375px] rounded-2xl p-6">
      <CongratsStep
        v-if="step === steps.CONGRATS"
        :current-savings="currentSavings"
        :savings-goal="savingsGoal"
        :reward-amount="rewardAmount"
        :progress="progress"
        :is-redeeming="isLoading"
        :minimum-threshold-met="props.minimumThresholdMet ?? true"
        @cancel="onClose"
        @redeem="onRedeem"
      />
      <VoucherGeneratedStep
        v-else-if="step === steps.VOUCHER_GENERATED"
        :current-savings="currentSavings"
        :savings-goal="savingsGoal"
        :reward-amount="rewardAmount"
        :progress="progress"
        :is-sending="isLoading"
        :minimum-threshold-met="props.minimumThresholdMet ?? true"
        @cancel="onClose"
        @send-email="onSendEmail"
      />
      <EmailSentStep
        v-else-if="step === steps.EMAIL_SENT"
        :merchant-name="order?.merchantName ?? ''"
        :merchant-link="order?.merchantLink ?? ''"
        @done="onClose"
      />
      <p v-if="errorMessage" class="text-sm font-medium text-error-500 text-center mt-2">{{ errorMessage }}</p>
    </DialogContent>
  </Dialog>
</template>

<script setup lang="ts">
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { NetworkError } from '@/errors';
import { amendOrder } from '@/services/orders';
import { addMetric, createException, createWarning } from '@/utils/monitoring.utils';
import { ref } from 'vue';
import type { ModelOrderResponse } from '@layup/api-sdk';
import CongratsStep from './CongratsStep.vue';
import EmailSentStep from './EmailSentStep.vue';
import VoucherGeneratedStep from './VoucherGeneratedStep.vue';

const props = defineProps<{
  open: boolean;
  order: ModelOrderResponse | undefined;
  currentSavings: number;
  savingsGoal: number;
  rewardAmount: number;
  progress: number;
  minimumThresholdMet?: boolean;
}>();

const emit = defineEmits<{
  (e: 'update:open', value: boolean): void;
  (e: 'redeemed'): void;
}>();

const steps = {
  CONGRATS: 1,
  VOUCHER_GENERATED: 2,
  EMAIL_SENT: 3,
};

const step = ref(steps.CONGRATS);
const isLoading = ref(false);
const errorMessage = ref('');

const onClose = () => {
  emit('update:open', false);
  setTimeout(() => {
    step.value = steps.CONGRATS;
    errorMessage.value = '';
  }, 300);
};

const onRedeem = async () => {
  if (!props.order) return;
  isLoading.value = true;
  errorMessage.value = '';

  try {
    const boostProduct = props.order.products?.find((p: any) => p.name === 'Boost Rewards');
    if (!boostProduct) throw new Error('This purchase is not a boost reward.');

    await amendOrder({
      orderId: props.order._id!,
      amendmentType: 'PRODUCT:UPDATE',
      products: [{ ...boostProduct, amount: props.currentSavings }],
    });

    addMetric('boost-redeem-voucher', { success: true });
    step.value = steps.VOUCHER_GENERATED;
  } catch (error) {
    if (error instanceof NetworkError) {
      createWarning(error);
      errorMessage.value = 'Network issue. Please try again.';
    } else {
      createException(error);
      errorMessage.value = 'Failed to redeem. Please contact support.';
    }
    addMetric('boost-redeem-voucher', { success: false, error: (error as Error).message });
  } finally {
    isLoading.value = false;
  }
};

const onSendEmail = () => {
  step.value = steps.EMAIL_SENT;
  emit('redeemed');
};
</script>
